"use client";

import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { LIVE_KEYS, type LiveKey, type LiveSnapshot } from "@/lib/live";
import { useMeActions, useMeContext } from "@/components/me-provider";

type LiveStore = {
  generation: Record<LiveKey, number>;
};

const LiveContext = createContext<LiveStore | null>(null);

const EMPTY_GENERATION: Record<LiveKey, number> = {
  today: 0,
  homework: 0,
  votes: 0,
  people: 0,
  kids: 0,
  timetable: 0,
};

function isEditing() {
  const node = document.activeElement;
  return node instanceof HTMLElement && Boolean(node.closest("input, textarea, select, [contenteditable='true']"));
}

export function LiveProvider({ children }: { children: React.ReactNode }) {
  const me = useMeContext();
  const { refreshMe } = useMeActions();
  const [generation, setGeneration] = useState(EMPTY_GENERATION);
  const previous = useRef<LiveSnapshot | null>(null);
  const refreshRef = useRef(refreshMe);
  refreshRef.current = refreshMe;
  const enabled = !me.user.isGuest && me.user.role !== "pending";

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    async function tick() {
      if (document.visibilityState !== "visible") return;
      try {
        const next = await api<LiveSnapshot>("/api/sync");
        if (cancelled) return;
        const prior = previous.current;
        previous.current = next;
        if (!prior) return;
        const changed = LIVE_KEYS.filter((key) => prior[key] !== next[key]);
        if (changed.length === 0) return;
        setGeneration((current) => {
          const copy = { ...current };
          for (const key of changed) copy[key] += 1;
          return copy;
        });
        void refreshRef.current().catch(() => undefined);
      } catch {
        // наступний пульс спробує ще раз
      }
    }

    void tick();
    const timer = window.setInterval(() => {
      void tick();
    }, 3000);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("focus", tick);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("focus", tick);
    };
  }, [enabled]);

  return <LiveContext.Provider value={{ generation }}>{children}</LiveContext.Provider>;
}

export function useLiveReload(key: LiveKey, reload: () => Promise<void>, active = true) {
  const store = useContext(LiveContext);
  const generation = store?.generation[key] ?? 0;
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const skip = useRef(true);
  const wasActive = useRef(active);

  useEffect(() => {
    if (skip.current) {
      skip.current = false;
      return;
    }
    if (isEditing()) {
      const retry = () => {
        if (isEditing()) return;
        document.removeEventListener("focusout", retry);
        void reloadRef.current().catch(() => undefined);
      };
      document.addEventListener("focusout", retry);
      return () => document.removeEventListener("focusout", retry);
    }
    void reloadRef.current().catch(() => undefined);
  }, [generation]);

  useEffect(() => {
    if (active && !wasActive.current) {
      void reloadRef.current().catch(() => undefined);
    }
    wasActive.current = active;
  }, [active]);
}
